import { useForm } from '@tanstack/react-form'
import { Loader2 } from 'lucide-react'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { PriceInput } from '@/components/ui/price-input'
import type { BoatLoan, CurtailmentFrequency } from '@/types/inventory'

export const loanFormSchema = z.object({
  lender: z.string().min(1, 'Lender is required').max(255),
  principal: z.coerce.number().gt(0, 'Principal must be greater than 0'),
  curtailment_amount: z.coerce.number().gte(0, 'Curtailment cannot be negative'),
  // Annual percentage (8.25 = 8.25% APR). 0 = interest not tracked — the
  // schedule generates curtailments only.
  interest_rate: z.coerce
    .number()
    .gte(0, 'Interest rate cannot be negative')
    .lte(100, 'Interest rate looks wrong (use APR %, e.g. 8.25)'),
  frequency: z.enum(['monthly', 'quarterly']),
  first_curtailment_on: z
    .string()
    .min(1, 'First curtailment date is required')
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use the date picker'),
  notes: z.string().max(5000, 'Notes are at most 5000 characters').optional(),
})

export type LoanFormValues = z.infer<typeof loanFormSchema>

const fieldSchemas = loanFormSchema.shape

interface LoanFormProps {
  /** Existing loan being edited (terms only — paid history never rewrites). */
  loan?: BoatLoan
  onSubmit: (values: LoanFormValues) => void
  isPending?: boolean
  submitLabel?: string
}

export function LoanForm({ loan, onSubmit, isPending, submitLabel = 'Save' }: LoanFormProps) {
  const initialValues: LoanFormValues = {
    lender: loan?.lender ?? '',
    principal: loan?.principal ?? 0,
    curtailment_amount: loan?.curtailment_amount ?? 0,
    interest_rate: loan?.interest_rate ?? 0,
    frequency: (loan?.frequency ?? 'monthly') as CurtailmentFrequency,
    first_curtailment_on: loan ? loan.first_curtailment_on.slice(0, 10) : '',
    notes: loan?.notes ?? '',
  }

  const form = useForm({
    defaultValues: initialValues,
    onSubmit: ({ value }) => {
      onSubmit({
        ...value,
        principal: Number(value.principal),
        curtailment_amount: Number(value.curtailment_amount),
        interest_rate: Number(value.interest_rate),
      })
    },
  })

  return (
    <Form form={form}>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void form.handleSubmit()
        }}
        className="space-y-4"
      >
        <FormField
          name="lender"
          validators={{ onChange: fieldSchemas.lender }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Lender</FormLabel>
              <FormControl>
                <Input placeholder="Shearwater Marine Finance" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <FormField
            name="principal"
            validators={{ onChange: fieldSchemas.principal }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Principal / Amount Financed ($)</FormLabel>
                <FormControl>
                  <PriceInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="150,000"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="curtailment_amount"
            validators={{ onChange: fieldSchemas.curtailment_amount }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Curtailment per Period ($)</FormLabel>
                <FormControl>
                  <PriceInput
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    placeholder="1,500"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <FormField
            name="interest_rate"
            validators={{ onChange: fieldSchemas.interest_rate }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Interest Rate (% APR)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    placeholder="8.25"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
                <p className="text-muted-foreground text-xs">
                  0 = not tracked. Interest is computed per period on the
                  remaining balance.
                </p>
              </FormItem>
            )}
          />
          <FormField
            name="frequency"
            validators={{ onChange: fieldSchemas.frequency }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Frequency</FormLabel>
                <Select onValueChange={field.onChange} value={field.value as string}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a frequency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="first_curtailment_on"
            validators={{ onChange: fieldSchemas.first_curtailment_on }}
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Curtailment Due</FormLabel>
                <FormControl>
                  <Input type="date" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          name="notes"
          validators={{ onChange: fieldSchemas.notes }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Notes</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Floor-plan terms, grace period, lender contact…"
                  className="h-24 resize-none"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </form>
    </Form>
  )
}
